import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { enumStatuses } from '../consts/status';
import { getStatusColor } from '../helpers/statusColor';
import PurchaseOrderStatusChip from '../features/purchaseOrders/PurchaseOrderStatusChip';

const StatusFilter = ({ 
  options, 
  selectedOptions, 
  onChange, 
  tableData,
  tableRef,
  placeholder, 
  testId,
  portal = false,
  purchaseOrderPalette = false,
  triggerClassName = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const portalMenuRef = useRef(null);
  const [dropdownStyles, setDropdownStyles] = useState(null);

  const toggleOption = (option) => {
    const newSelectedOptions = selectedOptions.includes(option)
      ? selectedOptions.filter(s => s !== option)
      : [...selectedOptions, option];
    onChange(newSelectedOptions);
  };

  useEffect(() => {
    updateDropdownPosition()
  }, [tableData])

  const selectAll = (e) => {
    e.stopPropagation();
    onChange([...options]);
  };

  const selectNone = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  const selectOnly = (e, option) => {
    e.stopPropagation();
    onChange([option]);
  };

  const calculateDistanceToTop = () => {
    let distanceToTop = 0;
    let element = dropdownRef.current;
    while (element) {
      distanceToTop += element.offsetTop;
      element = element.offsetParent;
    }
    return distanceToTop;
  };
  
  const updateDropdownPosition = () => { 
    if (dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;
      setDropdownStyles({
        top: portal ? rect.bottom : (calculateDistanceToTop() + dropdownRef.current.clientHeight) - scrollY,
        left: portal ? rect.left : rect.left + scrollX,
      });
    }
  }

  const handleOpen = () => {
    setIsOpen(!isOpen);
  }

  useEffect(() => {
    if (testId) return
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && !portalMenuRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    if (tableRef?.current) {
      tableRef.current.addEventListener("scroll", updateDropdownPosition);
    }

    document.addEventListener("scroll", updateDropdownPosition, true);
    window.addEventListener("resize", updateDropdownPosition);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener("scroll", updateDropdownPosition, true);
      window.removeEventListener("resize", updateDropdownPosition);
    };
  }, [isOpen, tableRef]);

  const menu = (
    <div ref={portal ? portalMenuRef : undefined} style={{ ...dropdownStyles, position: 'fixed' }} className="mt-2 w-64 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-[120]">
      <div className="py-1" role="menu" aria-orientation="vertical">
        <div className="px-4 py-2 text-sm text-gray-700 border-b border-gray-200 flex justify-center space-x-4">
          <button onClick={selectAll} className="text-blue-600 hover:underline" data-testid={testId ? `${testId}_select_all_statuses` : undefined}>Select All</button>
          <button onClick={selectNone} className="text-blue-600 hover:underline" data-testid={testId ? `${testId}_select_none_statuses` : undefined}>Select None</button>
        </div>
        {options?.map((option, index) => (
          <div key={option} className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 cursor-pointer">
            <input
              type="checkbox"
              checked={selectedOptions.includes(option)}
              data-testid={testId ? `${testId}_checkbox_${index}` : undefined}
              onChange={() => toggleOption(option)}
              className="mr-2"
            />
            {purchaseOrderPalette ? (
              <PurchaseOrderStatusChip status={option} />
            ) : (
              <span className={`inline-flex whitespace-nowrap items-center rounded-md px-2 py-0.5 text-xs font-medium capitalize ${enumStatuses[option] ? getStatusColor(option) : 'text-gray-700'}`}>{enumStatuses[option] || option}</span>
            )}
            <button onClick={(e) => selectOnly(e, option)} className="ml-auto text-gray-400 hover:underline">only</button>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="relative inline-block text-left" ref={dropdownRef} data-testid={testId}>
      <div>
        <button
          type="button"
          data-testid={testId ? `${testId}_open_statuses` : undefined}
          className={`inline-flex justify-between items-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 h-10 ${triggerClassName}`}
          onClick={handleOpen}
        >
          <span>{placeholder}</span>
          <ChevronDownIcon className="h-5 w-5 ml-2" aria-hidden="true" />
        </button>
      </div>

      {isOpen && (!portal || dropdownStyles) && (portal ? createPortal(menu, document.body) : menu)}
    </div>
  );
};

export default StatusFilter;
